/* Eén dag als grafiek op het formulier van een energieoplossing (ADR 0024): de lijnen van zon, batterij,
 * net en verbruik over 24 uur, met de grens van de aansluiting. Dit bestand tekent alleen. De vorm, het
 * lezen van een geplakte tabel, de regels van de site en wat er wordt geschreven, staan in data/dag.ts.
 *
 * Niemand typt honderden getallen: de reeksen komen uit een tabel die je plakt. Een tabel die te lezen
 * is, staat meteen in het formulier, met wat ervan begrepen is en een voorvertoning als lijntjes. Een
 * tabel die niet te lezen is, wordt niet overgenomen. Of de dag daarna ook klopt met de regels van de
 * site, zegt de regel onder de vakken; een dag die de site niet zou tonen, is niet op te slaan.
 *
 * De kolom hoort bij een script dat de eigenaar van de database draait: heeft de rij hem niet, dan
 * staat dat er, zoals bij "In het kort" (Aanvullingen.tsx). Een dag die niemand aanraakt, wordt bij
 * opslaan niet geschreven. */

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Gewijzigd, NaOpslaan, NietBeschikbaar } from './Aanvullingen';
import {
  KOLOM, LABEL, MINUTEN_PER_DAG, REEKSEN, SCRIPT, STAAT_ZIN, heeftDag, isLeeg, leesDag, leesGetal, leesTabel, samenvatting, staatVan, teSchrijven, toonGetal,
  zetGrens, zetTabel, zetTekst, type Lezing, type Reeks,
} from './data/dag';
import type { Rij } from './data/website';

const kw = (v: number): string => `${toonGetal(v)} kW`;

/* JSON met elke rij getallen op één regel: een reeks van 96 waarden is anders 96 regels. */
function leesbaar(w: unknown, inspring = ''): string {
  const dieper = `${inspring}  `;
  if (Array.isArray(w)) {
    if (w.every((v) => typeof v === 'number')) return `[${w.join(', ')}]`;
    return `[\n${w.map((v) => `${dieper}${leesbaar(v, dieper)}`).join(',\n')}\n${inspring}]`;
  }
  if (typeof w === 'object' && w !== null) {
    const sleutels = Object.keys(w);
    if (sleutels.length === 0) return '{}';
    return `{\n${sleutels.map((k) => `${dieper}${JSON.stringify(k)}: ${leesbaar((w as Record<string, unknown>)[k], dieper)}`).join(',\n')}\n${inspring}}`;
  }
  return JSON.stringify(w ?? null);
}

/* ── De voorvertoning ──────────────────────────────────────────────────────── */

/* Dezelfde vier lijnen als op de site, elk met een eigen kleur én een eigen vorm: het verbruik dun en
 * donker, het net dik, de zon gestreept, de batterij gestippeld. */
const LIJN: Record<Reeks, { kleur: string; dikte: number; streep?: string }> = {
  solar_kw: { kleur: '#c9800f', dikte: 1.5, streep: '6 4' },
  battery_kw: { kleur: '#089168', dikte: 2.5, streep: '1 5' },
  grid_kw: { kleur: '#4757b8', dikte: 3.5 },
  consumption_kw: { kleur: 'hsl(var(--foreground))', dikte: 1.5 },
};
const HOOGTE = 400;

function Sleutel({ reeks }: { reeks: Reeks }) {
  const { kleur, dikte, streep } = LIJN[reeks];
  return (
    <svg width="30" height="10" viewBox="0 0 30 10" aria-hidden="true" focusable="false" className="shrink-0">
      <line x1="2" y1="5" x2="28" y2="5" stroke={kleur} strokeWidth={dikte} strokeDasharray={streep} strokeLinecap="round" />
    </svg>
  );
}

/** Een ronde stap voor de schaal: 1, 2 of 5 maal een macht van tien, goed voor een stuk of vijf stappen. */
function rondeStap(bereik: number): number {
  if (!(bereik > 0)) return 1;
  const ruw = bereik / 5;
  const macht = 10 ** Math.floor(Math.log10(ruw));
  const deel = ruw / macht;
  return (deel <= 1 ? 1 : deel <= 2 ? 2 : deel <= 5 ? 5 : 10) * macht;
}

/** De vier reeksen als lijntjes, met de nullijn en de grens. Alleen om te zien of de tabel goed is
 *  gelezen: de site tekent de grafiek zelf, met een schaal en een beschrijving. `mis` is het moment
 *  waarop een regel van de site niet klopt, in minuten na middernacht. */
function Voorvertoning({ lezing, mis }: { lezing: Lezing; mis?: number }) {
  const aantal = lezing.reeksen.consumption_kw.length;
  if (aantal < 2 || REEKSEN.some(({ sleutel }) => lezing.reeksen[sleutel].length !== aantal)) return null;
  const stap = MINUTEN_PER_DAG / aantal;
  const grens = leesGetal(lezing.grens);
  const alles = REEKSEN.flatMap(({ sleutel }) => lezing.reeksen[sleutel]);
  // De randen van het vak zijn ronde getallen: zo zegt het getal in de hoek iets.
  const hoogste = Math.max(...alles, grens ?? 0, 0);
  const laagste = Math.min(...alles, 0);
  const stapKw = rondeStap(hoogste - laagste);
  const boven = Math.max(stapKw, Math.ceil((hoogste * 1.02) / stapKw) * stapKw);
  const onder = Math.floor((laagste * 1.02) / stapKw) * stapKw;
  const y = (v: number): number => Math.round(((boven - v) / (boven - onder)) * HOOGTE * 10) / 10;
  const punten = (rij: number[]): string => rij.map((v, i) => `${(i + 0.5) * stap},${y(v)}`).join(' ');
  return (
    <figure className="space-y-2" data-voorvertoning>
      <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-xs">
        {REEKSEN.map(({ sleutel, naam }) => <li key={sleutel} className="flex items-center gap-1.5"><Sleutel reeks={sleutel} /> {naam}</li>)}
        {grens !== null && grens > 0 && (
          <li className="flex items-center gap-1.5">
            <svg width="30" height="10" viewBox="0 0 30 10" aria-hidden="true" focusable="false" className="shrink-0"><line x1="2" y1="5" x2="28" y2="5" stroke="hsl(var(--destructive))" strokeWidth="1.5" strokeDasharray="5 3" /></svg>
            Grens van de aansluiting
          </li>
        )}
      </ul>
      <div className="relative">
        <svg viewBox={`0 0 ${MINUTEN_PER_DAG} ${HOOGTE}`} preserveAspectRatio="none" role="img" aria-label="Voorvertoning van de dag: zon, batterij, net en verbruik als lijnen"
          className="block h-44 w-full rounded-md border bg-background">
          {[6, 12, 18].map((uur) => <line key={uur} x1={uur * 60} x2={uur * 60} y1="0" y2={HOOGTE} stroke="hsl(var(--border))" strokeWidth="1" vectorEffect="non-scaling-stroke" />)}
          <line x1="0" x2={MINUTEN_PER_DAG} y1={y(0)} y2={y(0)} stroke="hsl(var(--muted-foreground))" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          {mis !== undefined && <line x1={mis + stap / 2} x2={mis + stap / 2} y1="0" y2={HOOGTE} stroke="#d97706" strokeWidth="2" vectorEffect="non-scaling-stroke" data-mis={mis} />}
          {grens !== null && grens > 0 && (
            <line x1="0" x2={MINUTEN_PER_DAG} y1={y(grens)} y2={y(grens)} stroke="hsl(var(--destructive))" strokeWidth="1.5" strokeDasharray="8 4" vectorEffect="non-scaling-stroke" />
          )}
          {REEKSEN.map(({ sleutel }) => (
            <polyline key={sleutel} points={punten(lezing.reeksen[sleutel])} fill="none" stroke={LIJN[sleutel].kleur} strokeWidth={LIJN[sleutel].dikte} strokeDasharray={LIJN[sleutel].streep}
              strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" data-lijn={sleutel} />
          ))}
        </svg>
        <span className="absolute left-1.5 top-1 rounded bg-background/80 px-1 text-[11px] tabular-nums text-muted-foreground">{kw(boven)}</span>
        {onder < 0 && <span className="absolute bottom-1 left-1.5 rounded bg-background/80 px-1 text-[11px] tabular-nums text-muted-foreground">{kw(onder)}</span>}
      </div>
      <div className="flex justify-between text-[11px] tabular-nums text-muted-foreground" aria-hidden="true">
        {['00:00', '06:00', '12:00', '18:00', '24:00'].map((tijd) => <span key={tijd}>{tijd}</span>)}
      </div>
      <figcaption className="text-xs text-muted-foreground">Een voorvertoning om te zien of de tabel goed is gelezen. De site tekent de grafiek zelf, met een schaal en een beschrijving in woorden.</figcaption>
    </figure>
  );
}

/* ── De kaart ──────────────────────────────────────────────────────────────── */

/** `vorige` is de rij in de database (bij iets nieuws niets), `waarde` het object in het concept. */
export function DagKaart({ vorige, waarde, fout, uit, zet }: { vorige: Rij | null; waarde: unknown; fout?: string; uit: boolean; zet: (waarde: unknown) => void }) {
  /* Wat er in het plakvak staat, hoort bij het scherm en niet bij de dag: een tabel die te lezen is,
   * gaat meteen het concept in en het vak is dan weer leeg. */
  const [plak, setPlak] = useState('');
  const [plakFout, setPlakFout] = useState<string | null>(null);
  const [gelezen, setGelezen] = useState<{ zin: string; letOp?: string } | null>(null);

  if (!vorige) return <NaOpslaan kaart={KOLOM} titel={LABEL} />;
  if (!heeftDag(vorige)) return <NietBeschikbaar kaart={KOLOM} titel={LABEL} script={SCRIPT} />;

  const opgeslagen = vorige[KOLOM];
  const basis = leesDag(opgeslagen);
  const lezing = basis.ok ? leesDag(waarde, true) : basis;
  if (!lezing.ok) {
    return (
      <Card data-kaart={KOLOM} data-kaart-staat="alleen-lezen">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">{LABEL} <Badge variant="secondary">Alleen lezen</Badge></CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Deze dag heeft een vorm die de bewerker niet kent. {lezing.reden} Daarom is hij hier niet te wijzigen. Hij blijft bij opslaan ongewijzigd.
          </p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-md border p-3 text-xs text-muted-foreground">{leesbaar(opgeslagen)}</pre>
        </CardContent>
      </Card>
    );
  }

  const plakIn = (invoer: string) => {
    setPlak(invoer);
    if (invoer.trim() === '') { setPlakFout(null); return; }
    const uitkomst = leesTabel(invoer);
    if ('fout' in uitkomst) { setPlakFout(uitkomst.fout); setGelezen(null); return; }
    const { tabel } = uitkomst;
    zet(zetTabel(waarde, tabel));
    setPlak('');
    setPlakFout(null);
    setGelezen({
      zin: `Tabel gelezen: ${tabel.reeksen.consumption_kw.length} punten per reeks, elke ${tabel.stap} minuten.${tabel.metTijd ? '' : ' De tijd stond er niet bij: de stap volgt uit het aantal regels.'}`,
      letOp: tabel.letOp,
    });
  };
  const wis = () => { setPlak(''); setPlakFout(null); setGelezen(null); };

  const gewijzigd = teSchrijven(opgeslagen, waarde) !== null || !!fout;
  const { staat, reden, minuut } = staatVan(opgeslagen, waarde);
  const begrepen = samenvatting(waarde);
  return (
    <Card data-kaart={KOLOM} data-kaart-staat={gewijzigd ? 'gewijzigd' : 'ongewijzigd'}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{LABEL}</CardTitle>
          {gewijzigd && <Gewijzigd uit={uit} terug={() => { wis(); zet(opgeslagen); }} />}
        </div>
        <p className="text-sm text-muted-foreground">
          De lijnen van zon, batterij, net en verbruik over één dag, met de grens van de aansluiting en wat de batterij opvangt. Op de site staat de grafiek onder de
          tekst van deze oplossing. Hij is bedoeld voor de pagina EMS: vul je hem bij een andere oplossing in, dan staat hij ook daar.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="flex max-w-3xl items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-700" data-let-op="klant">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Dit is een dag van een echte klant. Zet hem alleen op de site met toestemming van die klant. Noem de klant niet in de kop, het bijschrift of de bronregel:
            er komt geen naam op de site, tenzij Stan dat besluit.
          </span>
        </p>

        <div className="grid gap-3 lg:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${KOLOM}-title`}>Kop boven de grafiek *</Label>
            <Input id={`${KOLOM}-title`} value={lezing.kop} disabled={uit} onChange={(e) => zet(zetTekst(waarde, 'title', e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${KOLOM}-source`}>Bronregel onder de grafiek *</Label>
            <Input id={`${KOLOM}-source`} value={lezing.bron} disabled={uit} placeholder="Een dag bij een klant, september 2026, naam bekend bij Greenvision" onChange={(e) => zet(zetTekst(waarde, 'source', e.target.value))} />
            <p className="text-xs text-muted-foreground">Wat voor dag het is en uit welke maand. Geen naam van de klant.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${KOLOM}-caption`}>Bijschrift (mag leeg blijven)</Label>
            <Textarea id={`${KOLOM}-caption`} rows={2} value={lezing.bijschrift} disabled={uit} onChange={(e) => zet(zetTekst(waarde, 'caption', e.target.value))} />
            <p className="text-xs text-muted-foreground">Eén of twee zinnen onder de kop. Gewone tekst, geen HTML.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${KOLOM}-grens`}>Grens van de aansluiting in kW (mag leeg blijven)</Label>
            <Input id={`${KOLOM}-grens`} inputMode="decimal" value={lezing.grens} disabled={uit} placeholder="80" onChange={(e) => zet(zetGrens(waarde, e.target.value))} />
            <p className="text-xs text-muted-foreground">De site tekent hem als lijn in de grafiek. Leeg betekent: geen grenslijn.</p>
          </div>
        </div>

        <div className="space-y-3 rounded-md border p-3" data-dag-reeksen>
          <p className="text-sm font-medium">De dag zelf: vier reeksen in kW</p>
          {begrepen ? (
            <div className="space-y-3" data-begrepen>
              <p className="text-sm" data-punten={begrepen.punten ?? 'ongelijk'}>
                {begrepen.punten === null
                  ? 'De reeksen zijn niet even lang.'
                  : `${begrepen.punten} punten per reeks${begrepen.stap ? `, elke ${begrepen.stap} minuten` : ''}.`}
              </p>
              <div className="overflow-x-auto">
                {/* Op een telefoon staat het tijdstip onder de waarde: zo past de tabel in de breedte. */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-3 font-normal">Reeks</th><th className="py-1 pr-3 font-normal">Laagste</th><th className="py-1 font-normal">Hoogste</th></tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {begrepen.reeksen.map((r) => (
                      <tr key={r.sleutel} className="border-t" data-reeks={r.sleutel}>
                        <td className="py-1 pr-3 align-top"><span className="flex items-center gap-1.5"><Sleutel reeks={r.sleutel} /> {r.naam}</span></td>
                        <td className="py-1 pr-3 align-top">{kw(r.laagste)} <span className="block text-muted-foreground sm:inline">om {r.laagsteOm}</span></td>
                        <td className="py-1 align-top">{kw(r.hoogste)} <span className="block text-muted-foreground sm:inline">om {r.hoogsteOm}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Voorvertoning lezing={lezing} mis={minuut} />
            </div>
          ) : <p className="text-sm text-muted-foreground" data-begrepen="niets">Er staan nog geen reeksen. Plak hieronder de tabel met de dag.</p>}

          <div className="space-y-1.5">
            <Label htmlFor={`${KOLOM}-plak`}>{begrepen ? 'Plak een andere tabel om de reeksen te vervangen' : 'Plak hier de tabel met de dag'}</Label>
            <Textarea id={`${KOLOM}-plak`} rows={4} value={plak} disabled={uit} className="font-mono text-xs" onChange={(e) => plakIn(e.target.value)}
              placeholder={'Tijd\tZon\tBatterij\tNet\tVerbruik\n00:00\t0\t0\t12,5\t12,5\n00:15\t0\t0\t11,8\t11,8'} />
            <p className="text-xs text-muted-foreground">
              Kopieer in Excel de kolommen met de tijd en de vier reeksen, voor één hele dag vanaf 00:00, en plak ze hier. Met of zonder koppen; zonder koppen in de
              volgorde tijd, zon, batterij, net, verbruik. Een komma of een punt mag allebei. Alles in kW. Batterij: boven nul levert ze, onder nul laadt ze. Net: boven
              nul is afname, onder nul gaat er stroom terug. Het verbruik is op elk moment zon plus batterij plus net.
            </p>
            {gelezen && <p className="text-xs text-emerald-700" data-plak="gelezen">{gelezen.zin}</p>}
            {gelezen?.letOp && (
              <p className="flex items-start gap-1.5 text-xs text-amber-600" data-plak="let-op"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{gelezen.letOp}</span></p>
            )}
            {plakFout && <p className="text-xs text-destructive" role="alert" data-plak="fout">Niet overgenomen. {plakFout}</p>}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm" data-dag={staat}>{STAAT_ZIN[staat]}</p>
          {!isLeeg(waarde) && <Button type="button" variant="outline" size="sm" disabled={uit} onClick={() => { wis(); zet(null); }}>Maak de dag leeg</Button>}
        </div>
        {/* Een dag die er al stond en die de site niet toont: de reden staat erbij, ook als niemand hem aanraakt. */}
        {reden && !fout && <p className="text-xs text-amber-600" data-dag-reden>De site toont deze dag niet. {reden}</p>}
        {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
      </CardContent>
    </Card>
  );
}
